import { rejected, type CommandExecution } from '../core/commands';
import { createDocument, createRasterLayer, findLayer, findLayerParent, layerDocumentTransform, layerParentTransform, type EditorDocument, type GroupLayer, type Layer, type RasterLayer } from '../core/model';
import { invertMatrix } from '../core/geometry';
import type { DocumentSession } from '../core/session';
import { isSmartObject, RasterizeSmartObjectCommand, SMART_OBJECT_EXTENSION } from '../core/smart-objects';
import { SMART_FILTERS_EXTENSION } from '../core/smart-filters';
import { LAYER_STYLE_EXTENSION } from '../core/layer-styles';
import { RasterTileStore } from '../core/tiles';
import { inheritNativeDocumentFonts } from '../public-format/native-fonts';
import { inheritNativeDocumentImages } from '../public-format/native-images';
import { inheritNativeDocumentText } from '../public-format/native-text';
import { releaseNativeDocumentResources } from '../public-format/native-resources';
import type { Renderer } from './renderer';

/** A Smart Object rendered to a raster layer, not yet committed. */
export interface BakedSmartObject {
  source: GroupLayer;
  raster: RasterLayer;
}

/**
 * Renders the Smart Object `layerId` of `document`, as it is at this moment,
 * to a new raster layer without touching history: the first half of
 * `rasterizeSmartObjectInSession`. It is synchronous, so the semantic
 * `smartObject.rasterize` command renders inside its transaction and bakes
 * exactly the state the transaction's earlier steps left.
 */
export function bakeSmartObject(document: EditorDocument, layerId: string, renderer: Pick<Renderer, 'flatten' | 'disposeDocument'>): CommandExecution | BakedSmartObject {
  const source = findLayer(document.root, layerId);
  if (!isSmartObject(source)) return rejected('Select a Smart Object to rasterize.');
  for (let layer: Layer | undefined = source; layer; layer = findLayerParent(document.root, layer.id)?.parent) {
    if (layer.locked) return rejected('Unlock the Smart Object and its parent groups before rasterizing.');
  }
  const parent = layerParentTransform(document.root, source.id);
  const transform = layerDocumentTransform(document.root, source.id);
  if (!transform?.every(Number.isFinite)) return rejected('The Smart Object transform is invalid. Restore a finite transform before rasterizing.');
  const inverse = parent && invertMatrix(parent);
  if (!inverse || !inverse.every(Number.isFinite)) return rejected('The parent transform cannot be inverted. Restore a nonzero scale before rasterizing.');
  const projection = createDocument({ name: `${source.name} rasterize`, width: document.width, height: document.height, background: 'transparent' });
  projection.id = document.id; projection.extensions = document.extensions;
  const extensions = { ...source.extensions }; delete extensions[LAYER_STYLE_EXTENSION];
  projection.root.children = [{ ...source, visible: true, opacity: 1, fillOpacity: 1, blendMode: 'normal', clipping: false, transform, extensions }];
  projection.activeLayerIds = [source.id];
  let raster: RasterLayer;
  try {
    inheritNativeDocumentFonts(document, projection);
    inheritNativeDocumentImages(document, projection);
    inheritNativeDocumentText(document, projection);
    const canvas = renderer.flatten(projection);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Canvas 2D is unavailable.');
    raster = createRasterLayer(`${source.name} rasterized`, canvas.width, canvas.height);
    // These pixels are already in document coordinates. Cancel the parent's
    // transform when inserting them at the same tree position, avoiding a
    // second transform/resample at the current document resolution.
    raster.transform = inverse;
    raster.raster = RasterTileStore.fromImageData(context.getImageData(0, 0, canvas.width, canvas.height));
    raster.visible = source.visible; raster.opacity = source.opacity; raster.fillOpacity = source.fillOpacity;
    raster.blendMode = source.blendMode; raster.clipping = source.clipping;
    raster.locked = source.locked; raster.pixelsLocked = source.pixelsLocked; raster.positionLocked = source.positionLocked; raster.color = source.color;
    raster.extensions = structuredClone(source.extensions);
    delete raster.extensions[SMART_OBJECT_EXTENSION]; delete raster.extensions[SMART_FILTERS_EXTENSION];
    // The group mask and smart filters are in the pixels; its style and outer
    // blend/opacity remain on the layer, in their original compositing order.
  } finally {
    try { renderer.disposeDocument(projection); } finally { releaseNativeDocumentResources(projection); }
  }
  return { source, raster };
}

/** Bake the current document-resolution surface. Keep outer compositing live so
 * blending/clipping still see their original siblings. Source resources remain
 * owned by the session for exact undo; the temporary view only borrows them. */
export function rasterizeSmartObjectInSession(session: DocumentSession, layerId: string, renderer: Pick<Renderer, 'flatten' | 'disposeDocument'>): CommandExecution {
  const captured = session.captureRevision(); const document = captured.document;
  const baked = bakeSmartObject(document, layerId, renderer);
  if ('outcome' in baked) return baked;
  // Publish only after rendering, pixel capture and cleanup have all succeeded.
  if (!session.isCurrent(captured) || findLayer(document.root, layerId) !== baked.source) return rejected('The document changed while rasterizing. Try again.');
  return session.history.execute(document, new RasterizeSmartObjectCommand(layerId, baked.raster));
}
